import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { appeler } from '../../api/client.ts';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Chargement, DateHeure, Erreur } from '../../composants/communs.tsx';
import { DataTable, Montant, Nombre } from '../../composants/ui/index.ts';
import { DESTINATIONS, REMBOURSEMENTS, type RetourDetail } from './types.ts';

/** Route de détail : `/retours/:id`. */
export function RetourDetailVue(): ReactNode {
  const { id } = useParams<{ id: string }>();

  return (
    <>
      <Entete
        titre="Détail du retour"
        sous="Motif, articles repris, et IMEI effectivement rendus"
        actions={<Link to="/retours">← Retour aux retours</Link>}
      />
      {id !== undefined && <Contenu retourId={id} />}
    </>
  );
}

function nomComplet(nom: string | null, prenom: string | null): string | null {
  const morceaux = [nom, prenom].filter((m): m is string => m !== null && m !== '');
  return morceaux.length === 0 ? null : morceaux.join(' ');
}

function Contenu({ retourId }: { retourId: string }): ReactNode {
  const { data, isPending, error } = useQuery({
    queryKey: ['retour', retourId],
    queryFn: () => appeler<RetourDetail>(`/retours/${retourId}`),
  });

  if (isPending) return <Chargement />;
  if (error !== null) return <Erreur erreur={error} />;

  const client = nomComplet(data.client_nom, data.client_prenom);

  return (
    <div className="pile">
      <section className="vente-section">
        <div className="fiche-table-tete">
          <h2 style={{ margin: 0 }}>{data.numero}</h2>
          <span className="fiche-compteur">
            <DateHeure valeur={data.horodatage_serveur} />
          </span>
        </div>

        <div className="pile" style={{ gap: 4 }}>
          <div className="ligne" style={{ justifyContent: 'space-between' }}>
            <span>Vente d’origine</span>
            <Link to={`/ventes/${data.vente_id}`} className="mono">
              {data.vente_numero}
            </Link>
          </div>
          {client !== null && (
            <div className="ligne" style={{ justifyContent: 'space-between' }}>
              <span>Client</span>
              <span>{client}</span>
            </div>
          )}
          <div className="ligne" style={{ justifyContent: 'space-between' }}>
            <span>Enregistré par</span>
            <span>{data.enregistre_par}</span>
          </div>
          {data.boutique !== data.boutique_origine && (
            <div className="ligne" style={{ justifyContent: 'space-between' }}>
              <span>Reçu à / vendu depuis</span>
              <span>
                {data.boutique} / {data.boutique_origine}
              </span>
            </div>
          )}
          {data.hors_fenetre && (
            <p className="message message--attention" role="note">
              <strong>Dérogation</strong> — ce retour a été accepté hors de la fenêtre normale.
            </p>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <h3 style={{ marginBottom: 4 }}>Motif</h3>
          <p>{data.motif}</p>
        </div>
      </section>

      <section className="vente-section">
        <div className="fiche-table-tete">
          <h2 style={{ margin: 0 }}>Articles</h2>
        </div>

        <DataTable
          lignes={data.lignes}
          cleDe={(ligne) => ligne.produit_id}
          vide="Ce retour ne porte aucune ligne."
          colonnes={[
            {
              cle: 'designation',
              entete: 'Produit',
              rendu: (l) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{l.designation}</div>
                  {l.imeis
                    .filter((paire) => paire.imei !== null || paire.imei2 !== null)
                    .map((paire, index) => (
                      <div key={index} className="fiche-produit__reference">
                        IMEI : {paire.imei ?? ''}
                        {paire.imei2 !== null && paire.imei2 !== '' ? ` / ${paire.imei2}` : ''}
                      </div>
                    ))}
                </div>
              ),
            },
            {
              cle: 'quantite',
              entete: 'Qté',
              numerique: true,
              rendu: (l) => <Nombre valeur={l.quantite} />,
            },
            {
              cle: 'prix',
              entete: 'Prix unitaire',
              numerique: true,
              rendu: (l) => <Montant valeur={l.prix_unitaire_net} />,
            },
            {
              cle: 'destination',
              entete: 'Destination',
              rendu: (l) => DESTINATIONS[l.destination],
            },
            {
              cle: 'total',
              entete: 'Total',
              numerique: true,
              rendu: (l) => <Montant valeur={l.quantite * l.prix_unitaire_net} />,
            },
          ]}
        />

        <div className="pile" style={{ gap: 6, maxWidth: 320, marginLeft: 'auto', marginTop: 12 }}>
          <div
            className="ligne"
            style={{
              justifyContent: 'space-between',
              borderTop: '1px solid var(--trait)',
              paddingTop: 10,
              fontWeight: 700,
            }}
          >
            <span>Total</span>
            <span style={{ fontSize: '1.25rem' }}>
              <Montant valeur={data.montant} total />
            </span>
          </div>
          <div className="ligne" style={{ justifyContent: 'space-between' }}>
            <span>Remboursement</span>
            <span>{REMBOURSEMENTS[data.remboursement]}</span>
          </div>
        </div>
      </section>
    </div>
  );
}
